"use client";

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, X, Send, Mic, MicOff, Maximize2, Minimize2, Loader2, Sparkles, MessageSquare, ChevronDown, Plus, Paperclip, Image as ImageIcon, Zap, Globe, Cloud, Database } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import { useApp } from '@/hooks/use-app';
import { usePathname, useRouter, useParams } from 'next/navigation';
import { agentSync, AgentMessage, asContent } from '@/lib/state/agent-sync';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SmartCardUI from '@/components/smartcard-ui';
import { cn } from '@/lib/utils';
import { GLASS_PANEL, GLASS_HEADER, GLASS_SECONDARY_BUBBLE, GLASS_INPUT } from '@/lib/ui-classes';
import { useStore } from '@/lib/state/store';
import { compressImage } from '@/lib/image-utils';
import Image from 'next/image';
import { useUnitOfWork, useEquipment } from '@/lib/data/provider';
import { useToast } from '@/hooks/use-toast';
import { withMeta } from '@/lib/data/helpers';
import { ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem } from '@/components/ui/context-menu';
import { FieldWorkOrderPad } from '@/components/workorders/FieldWorkOrderPad';
import { WorkOrder } from '@/lib/types';
import { Wrench } from 'lucide-react';
import { getDashboardCreatePath } from '@/lib/routes/dashboard-routes';
import { HeatherContextMenu } from './heather-sync-badge';
import { executeHeatherAction } from './heather-action-handler';

/**
 * cleanMessageContent
 * Normalizes all LangGraph/LangChain content shapes into a plain readable string.
 *
 * The backend may return content as:
 *   - A plain `string` (most common)
 *   - An array of content blocks: `[{type: "text", text: "..."}]`
 *   - An object with a `.text` key: `{text: "..."}`
 *   - A raw JSON structure (last resort fallback)
 *
 * This prevents raw metadata like `[{"type":"ai","content":[...]}]` from
 * leaking into the chat UI.
 *
 * @param content - Raw content from the agent API response
 * @returns A clean, human-readable string suitable for display
 */
function cleanMessageContent(content: unknown): string {
  if (typeof content === 'string') {
    return content;
  }
  if (Array.isArray(content)) {
    // LangGraph content block array: [{type: "text", text: "..."}]
    const textBlocks = content
      .filter((block): block is { type: string; text: string } =>
        typeof block === 'object' && block !== null && (block as any).type === 'text' && typeof (block as any).text === 'string'
      )
      .map((block) => block.text);
    if (textBlocks.length > 0) return textBlocks.join('\n');
    // Fallback: try to get any string value from the array
    const firstString = content.find((b) => typeof b === 'string');
    if (firstString) return firstString as string;
  }
  if (typeof content === 'object' && content !== null) {
    // Object with a .text key
    if ('text' in content && typeof (content as any).text === 'string') {
      return (content as any).text;
    }
    // Object with a .content key (nested LangGraph wrapper)
    if ('content' in content) {
      return cleanMessageContent((content as any).content);
    }
  }
  // Last resort: stringify so something is shown rather than a crash
  return JSON.stringify(content);
}

export function HeatherOverlay() {
    const { currentUser, userPhotoUrl, showAlert } = useApp();
    const pathname = usePathname();
    const params = useParams();
    const router = useRouter();
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isSideView, setIsSideView] = useState(false);
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState<AgentMessage[]>([]);
    const [isThinking, setIsThinking] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [attachments, setAttachments] = useState<{ type: 'image' | 'file'; url: string; name: string }[]>([]);
    const [isDragging, setIsDragging] = useState(false);

    const scrollRef = useRef<HTMLDivElement>(null);
    const recognitionRef = useRef<any>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { responseStyle, selectedVoice, brainMode, setBrainMode } = useStore();
    const uow = useUnitOfWork();
    const equipmentService = useEquipment();
    const { toast } = useToast();

    // Work order sheet state
    const [woSheetOpen, setWoSheetOpen] = useState(false);
    const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
    const [activeWoId, setActiveWoId] = useState<string | null>(null);

    const loadWorkOrders = useCallback(async () => {
        try {
            const data = (await uow.workOrders.list()) as WorkOrder[];
            setWorkOrders(data);
        } catch { /* offline-safe */ }
    }, [uow.workOrders]);

    const handleOpenWorkOrders = useCallback(() => {
        setActiveWoId(null);
        loadWorkOrders();
        setWoSheetOpen(true);
    }, [loadWorkOrders]);

    useEffect(() => {
        const onOpenWorkOrders = (event: Event) => {
            const custom = event as CustomEvent<{ mode?: 'list' | 'new' | 'overlay' }>;
            const mode = custom.detail?.mode || 'list';

            if (mode === 'overlay') {
                handleOpenWorkOrders();
                return;
            }

            setIsOpen(false);
            setWoSheetOpen(false);
            router.push(mode === 'new' ? '/dashboard/work-orders/new' : '/dashboard/work-orders');
        };

        window.addEventListener('heather-overlay:open-work-orders', onOpenWorkOrders);
        return () => window.removeEventListener('heather-overlay:open-work-orders', onOpenWorkOrders);
    }, [router, handleOpenWorkOrders]);

    const handleUpdateWorkOrder = async (wo: WorkOrder) => {
        await uow.workOrders.put(withMeta(wo));
        await loadWorkOrders();
    };

    const handleDeleteWorkOrder = async (woId: string) => {
        await uow.workOrders.remove(woId);
        if (activeWoId === woId) setActiveWoId(null);
        await loadWorkOrders();
    };

    const handleAction = async (msgId: string, actionId: string, values: any) => {
        await executeHeatherAction(msgId, actionId, values, {
            messages,
            equipmentService,
            router,
            currentUser,
            toast: (opts) => showAlert(opts.description, opts.variant === 'destructive' ? 'destructive' : 'default'),
            setMessages,
            uow
        });
    };

    // 1. Sync messages with local store
    useEffect(() => {
        if (!currentUser) return;

        const sync = async () => {
            const msgs = await agentSync.listMessages(currentUser, 'direct');
            setMessages(prev => {
                if (JSON.stringify(prev) !== JSON.stringify(msgs)) return msgs;
                return prev;
            });

            // Check for thinking state
            const lastMsg = msgs[msgs.length - 1];
            if (lastMsg && lastMsg.sender === 'agent' && lastMsg.status === 'executing') {
                setIsThinking(true);
            } else {
                setIsThinking(false);
            }
        };

        sync();
        const interval = setInterval(sync, 2000);
        return () => clearInterval(interval);
    }, [currentUser]);

    // Allow other UI entry points to open this contextual overlay.
    useEffect(() => {
        const onOpenOverlay = (event: Event) => {
            const custom = event as CustomEvent<{ sideView?: boolean; minimized?: boolean; prefill?: string }>;
            setIsOpen(true);
            setIsMinimized(!!custom.detail?.minimized);
            setIsSideView(!!custom.detail?.sideView);
            if (custom.detail?.prefill) setInput(custom.detail.prefill);
        };

        window.addEventListener('heather-overlay:open', onOpenOverlay as EventListener);
        return () => window.removeEventListener('heather-overlay:open', onOpenOverlay as EventListener);
    }, []);

    // 2. Auto-scroll to bottom only when message length changes, thinking state changes, or overlay is opened
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages.length, isThinking, isOpen]);

    // 3. Handle Send
    const handleSend = async (overrideText?: string) => {
        const text = overrideText || input;
        if (!text.trim() && attachments.length === 0) return;
        if (!currentUser) return;

        const currentInput = text;
        const currentAttachments = [...attachments];

        setInput('');
        setAttachments([]);
        setIsThinking(true);

        const userMsg: AgentMessage = {
            id: crypto.randomUUID(),
            createdAt: Date.now(),
            sender: currentUser,
            recipient: 'agent',
            status: 'sent',
            content: currentInput,
            channel: 'direct',
            attachments: currentAttachments.map(a => ({ type: a.type, url: a.url, name: a.name })),
            meta: {
                pathname,
                params: params as any,
                activeId: params?.id || params?.workOrderId || params?.projectId,
                source: 'overlay',
                timestamp: Date.now()
            }
        };

        await agentSync.addMessage(userMsg);
        setMessages(prev => [...prev, userMsg]);

        try {
            const res = await fetch("/api/mcp-architect/invoke", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    input: {
                        messages: [{ type: "human", content: currentInput }],
                        voice_preference: selectedVoice
                    },
                    config: {
                        configurable: {
                            session_id: currentUser,
                            brain_mode: brainMode,
                            response_style: responseStyle,
                            context: {
                                current_page: pathname,
                                page_params: params,
                                active_id: params?.id || params?.workOrderId || params?.projectId,
                                app_state: "active",
                                platform: "overlay_v2",
                                attachments: currentAttachments
                            }
                        }
                    }
                }),
            });

            if (!res.ok) throw new Error("Backend unavailable");

            const data = await res.json().catch(() => null);
            const outputMessages = data?.output?.messages || data?.messages || [];

            let lastAiMsg: any = null;
            for (let i = outputMessages.length - 1; i >= 0; i--) {
                if (outputMessages[i]?.type === 'ai') {
                    lastAiMsg = outputMessages[i];
                    break;
                }
            }

            const fallbackText = typeof data?.output_text === 'string' ? data.output_text : null;
            const rawReplyContent = lastAiMsg?.content || fallbackText;
            // Normalize content — backend may return arrays of content blocks rather than plain strings
            const replyContent = rawReplyContent != null ? cleanMessageContent(rawReplyContent) : null;

            if (replyContent) {
                const replyMsg: AgentMessage = {
                    id: crypto.randomUUID(),
                    createdAt: Date.now(),
                    sender: 'agent',
                    recipient: currentUser,
                    status: 'received',
                    content: replyContent,
                    meta: {
                        source: 'overlay',
                        current_page: pathname,
                        page_params: params as any,
                    }
                };

                await agentSync.addMessage(replyMsg);
                setMessages(await agentSync.listMessages(currentUser, 'direct'));
            }
            setIsThinking(false);
        } catch (err) {
            console.error(err);
            setIsThinking(false);
            showAlert("Heather is temporarily unavailable.", "destructive");
        }
    };

    // 4. File Handling
    const processFile = async (file: File) => {
        let url = "";
        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            const rawUrl = await new Promise<string>((resolve) => {
                reader.onload = (e) => resolve(e.target?.result as string);
                reader.readAsDataURL(file);
            });
            url = await compressImage(rawUrl, { maxWidth: 1024, quality: 0.7 });
        } else {
            url = "file-icon-placeholder";
        }

        setAttachments(prev => [...prev, {
            type: file.type.startsWith('image/') ? 'image' : 'file',
            url,
            name: file.name
        }]);
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) processFile(file);
    };

    // 5. Voice Input
    const toggleMic = () => {
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (!SpeechRecognition) {
            showAlert("Speech recognition not supported in this browser.", "destructive");
            return;
        }

        if (isListening) {
            recognitionRef.current?.stop();
            setIsListening(false);
        } else {
            const recognition = new SpeechRecognition();
            recognitionRef.current = recognition;
            recognition.continuous = false;
            recognition.interimResults = true;
            recognition.lang = 'en-US';

            recognition.onstart = () => setIsListening(true);
            recognition.onresult = (event: any) => {
                const transcript = event.results[0][0].transcript;
                setInput(transcript);
                if (event.results[0].isFinal) {
                    handleSend(transcript);
                    setIsListening(false);
                }
            };
            recognition.onerror = () => setIsListening(false);
            recognition.onend = () => setIsListening(false);

            try { recognition.start(); } catch (e) { console.error(e); }
        }
    };

    if (!currentUser) return null;

    return (
        <div
            className="fixed bottom-4 right-4 z-[9600] flex flex-col items-end gap-3 pointer-events-none"
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                const file = e.dataTransfer.files[0];
                if (file) processFile(file);
                if (!isOpen) setIsOpen(true);
            }}
        >
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20, transformOrigin: 'bottom right' }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        className={cn(
                            "pointer-events-auto transition-all duration-300 ease-in-out relative",
                            isMinimized
                                ? "h-14 w-60"
                                : isSideView
                                    ? "h-[calc(100vh-2rem)] w-[420px] md:w-[520px]"
                                    : "h-[500px] w-80 md:w-96"
                        )}
                    >
                        {isDragging && !isMinimized && (
                            <div className="absolute inset-0 z-[110] bg-primary/20 backdrop-blur-sm rounded-2xl border-4 border-dashed border-primary flex flex-col items-center justify-center gap-2 m-1">
                                <Plus className="h-10 w-10 text-primary animate-bounce" />
                                <p className="text-primary font-black uppercase tracking-widest text-sm">Drop to Sync</p>
                            </div>
                        )}

                        <Card className={`h-full flex flex-col ${GLASS_PANEL} overflow-hidden rounded-2xl`}>
                            {/* Header */}
                            <CardHeader className={`p-3 ${GLASS_HEADER} flex flex-row items-center justify-between shrink-0`}>
                                <div className="flex items-center gap-2">
                                    <div className="relative">
                                        <HeatherContextMenu variant="personal" onOpenWorkOrders={handleOpenWorkOrders}>
                                            <Avatar className="h-10 w-10 border-2 border-primary/40 p-0.5 shadow-[0_0_10px_rgba(193,255,0,0.2)] cursor-pointer">
                                                <AvatarImage src="/icons/heather/icon1.png" className="rounded-full object-cover" />
                                                <AvatarFallback>H</AvatarFallback>
                                            </Avatar>
                                        </HeatherContextMenu>
                                        <div className="absolute top-0 -right-0.5 w-3 h-3 bg-[#c1ff00] rounded-full border-2 border-black animate-pulse" />
                                    </div>
                                    <div className="flex flex-col">
                                        <h3 className="text-xs font-black uppercase tracking-[0.1em] text-white flex items-center gap-1.5">
                                            Heather
                                            <div className="w-1 h-1 rounded-full bg-primary" />
                                        </h3>
                                        <p className="text-[9px] text-white/40 font-bold uppercase tracking-widest truncate max-w-[140px]">
                                            {pathname === '/' ? 'Monitoring Home' : `Context: ${pathname.split('/').pop()}`}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    {!isMinimized && (
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="sm" className="h-8 px-2 rounded-full hover:bg-white/5 text-white/60 text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 pointer-events-auto">
                                                    <span>Brain: {brainMode}</span>
                                                    <ChevronDown className="h-3 w-3" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent className="bg-black/95 border-white/10 backdrop-blur-xl text-white rounded-xl py-1 z-[9990] pointer-events-auto">
                                                <DropdownMenuLabel className="text-[10px] uppercase font-mono tracking-wider text-white/40 px-2 py-1">Select Brain</DropdownMenuLabel>
                                                <DropdownMenuSeparator className="bg-white/10" />
                                                <DropdownMenuItem onClick={() => setBrainMode('Auto')} className={cn("cursor-pointer focus:bg-white/10 text-xs gap-2 flex items-center justify-between", brainMode === 'Auto' && "text-[#c1ff00] bg-white/5")}>
                                                    <span className="flex items-center gap-2"><Zap className="h-3.5 w-3.5 text-yellow-400" /> Auto</span>
                                                    {brainMode === 'Auto' && <span className="h-1.5 w-1.5 rounded-full bg-[#c1ff00]" />}
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => setBrainMode('Cloud')} className={cn("cursor-pointer focus:bg-white/10 text-xs gap-2 flex items-center justify-between", brainMode === 'Cloud' && "text-[#c1ff00] bg-white/5")}>
                                                    <span className="flex items-center gap-2"><Cloud className="h-3.5 w-3.5 text-blue-400" /> Cloud</span>
                                                    {brainMode === 'Cloud' && <span className="h-1.5 w-1.5 rounded-full bg-[#c1ff00]" />}
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => setBrainMode('Local')} className={cn("cursor-pointer focus:bg-white/10 text-xs gap-2 flex items-center justify-between", brainMode === 'Local' && "text-[#c1ff00] bg-white/5")}>
                                                    <span className="flex items-center gap-2"><Database className="h-3.5 w-3.5 text-emerald-400" /> Local</span>
                                                    {brainMode === 'Local' && <span className="h-1.5 w-1.5 rounded-full bg-[#c1ff00]" />}
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    )}
                                    {!isMinimized && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 px-2 rounded-full hover:bg-white/5 text-white/60 text-[10px] font-black uppercase tracking-wider pointer-events-auto"
                                            onClick={() => {
                                                router.push('/heather/office');
                                                setIsOpen(false);
                                                setIsMinimized(false);
                                                setIsSideView(false);
                                            }}
                                            title="Open Heather Office"
                                        >
                                            Office
                                        </Button>
                                    )}
                                    {!isMinimized && (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 rounded-full hover:bg-white/5 text-white/50"
                                            onClick={() => setIsSideView(!isSideView)}
                                            title={isSideView ? 'Use compact view' : 'Expand side view'}
                                        >
                                            {isSideView ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                                        </Button>
                                    )}
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 rounded-full hover:bg-white/5 text-white/50"
                                        onClick={() => {
                                            if (!isMinimized) setIsSideView(false);
                                            setIsMinimized(!isMinimized);
                                        }}
                                    >
                                        {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 rounded-full hover:bg-red-500/10 hover:text-red-500 text-white/50"
                                        onClick={() => {
                                            setIsOpen(false);
                                            setIsMinimized(false);
                                            setIsSideView(false);
                                        }}
                                    >
                                        <X className="h-5 w-5" />
                                    </Button>
                                </div>
                            </CardHeader>

                            {/* Main Chat View */}
                            {!isMinimized && (
                                <>
                                    <CardContent className="flex-1 overflow-hidden p-0 relative bg-gradient-to-b from-black/0 to-primary/5">
                                        <ScrollArea className="h-full p-4">
                                            <div className="space-y-5 pb-4">
                                                {messages.length === 0 && (
                                                    <div className="text-center py-12 px-6 space-y-3 opacity-30">
                                                        <div className="w-12 h-12 mx-auto rounded-full border-2 border-dashed border-white/20 flex items-center justify-center">
                                                            <Avatar className="h-8 w-8">
                                                                <AvatarImage src="/icons/heather/icon1.png" className="object-cover opacity-50 grayscale" />
                                                            </Avatar>
                                                        </div>
                                                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white">System Standby</p>
                                                        <p className="text-[9px] text-white italic">&quot;Heather is following your workflow. Ask her to help with this page.&quot;</p>
                                                    </div>
                                                )}
                                                {messages.map((msg) => {
                                                    const isAgent = msg.sender === 'agent';
                                                    return (
                                                        <div key={msg.id} className={cn("flex gap-3", isAgent ? "flex-row" : "flex-row-reverse")}>
                                                            <Avatar className={cn("h-7 w-7 mt-1 flex-shrink-0 border", isAgent ? "border-primary/20" : "border-white/10")}>
                                                                <AvatarImage src={isAgent ? "/icons/heather/icon1.png" : (userPhotoUrl || "")} className="object-cover" />
                                                                <AvatarFallback className="text-[9px] bg-black">{isAgent ? 'H' : 'U'}</AvatarFallback>
                                                            </Avatar>
                                                            <div className={cn(
                                                                "max-w-[82%] rounded-2xl px-3.5 py-2.5 text-sm transition-all duration-300",
                                                                isAgent
                                                                    ? `${GLASS_SECONDARY_BUBBLE} text-white/90 border border-white/5 shadow-sm`
                                                                    : "bg-primary text-black font-semibold shadow-lg shadow-primary/10 rounded-tr-sm"
                                                            )}>
                                                                {/* Attachments */}
                                                                {msg.attachments && msg.attachments.length > 0 && (
                                                                    <div className="flex flex-wrap gap-2 mb-2">
                                                                        {msg.attachments.map((att, i) => (
                                                                            <div key={i} className="relative rounded overflow-hidden border border-white/10 bg-black/40">
                                                                                {att.type === 'image' ? (
                                                                                    <img src={att.url} alt="attachment" className="object-cover max-h-40 w-auto rounded" />
                                                                                ) : (
                                                                                    <div className="flex items-center gap-2 p-2">
                                                                                        <Paperclip className="h-3 w-3" />
                                                                                        <span className="text-[10px] truncate max-w-[100px]">{att.name || 'File'}</span>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}

                                                                {isAgent && asContent(msg.content)?.recipe ? (
                                                                    <div className="scale-90 origin-top-left -ml-2 -mt-1">
                                                                        <SmartCardUI
                                                                            recipe={asContent(msg.content)!.recipe!}
                                                                            onAction={(actionId, vals) => handleAction(msg.id!, actionId, vals)}
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <div className={cn(
                                                                        "prose prose-xs break-words",
                                                                        isAgent ? "prose-invert" : "text-black"
                                                                    )}>
                                                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                                            {cleanMessageContent(msg.content)}
                                                                        </ReactMarkdown>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {isThinking && (
                                                    <div className="flex gap-3">
                                                        <div className="relative">
                                                            <Avatar className="h-7 w-7 border border-primary/40">
                                                                <AvatarImage src="/icons/heather/icon1.png" className="object-cover" />
                                                            </Avatar>
                                                            <div className="absolute inset-0 rounded-full border border-primary animate-ping opacity-50" />
                                                        </div>
                                                        <div className={`${GLASS_SECONDARY_BUBBLE} rounded-2xl px-4 py-2 flex items-center gap-2.5 border border-white/5`}>
                                                            <div className="flex gap-1">
                                                                <div className="w-1 h-1 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]" />
                                                                <div className="w-1 h-1 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]" />
                                                                <div className="w-1 h-1 bg-primary rounded-full animate-bounce" />
                                                            </div>
                                                            <span className="text-[10px] font-black text-primary/60 uppercase tracking-widest">Processing</span>
                                                        </div>
                                                    </div>
                                                )}
                                                <div ref={scrollRef} />
                                            </div>
                                        </ScrollArea>
                                    </CardContent>

                                    {/* Footer */}
                                    <CardFooter className="p-3 border-t border-white/5 bg-black shrink-0 flex flex-col gap-2">
                                        {/* Attachment Previews */}
                                        {attachments.length > 0 && (
                                            <div className="flex flex-wrap gap-2 w-full mb-1">
                                                {attachments.map((att, i) => (
                                                    <div key={i} className="relative group animate-in zoom-in-50 duration-200">
                                                        <div className="h-12 w-12 rounded border border-white/10 overflow-hidden bg-white/5">
                                                            {att.type === 'image' ? (
                                                                <img src={att.url} alt="preview" className="h-full w-full object-cover" />
                                                            ) : (
                                                                <div className="flex items-center justify-center h-full">
                                                                    <Paperclip className="h-4 w-4 text-white/40" />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <button
                                                            onClick={() => setAttachments(attachments.filter((_, idx) => idx !== i))}
                                                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 shadow-md"
                                                        >
                                                            <X className="h-2 w-2" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <div className="flex gap-2 w-full items-center">
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                className="hidden"
                                                onChange={handleFileUpload}
                                                accept="image/*"
                                            />
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-10 w-10 shrink-0 rounded-xl text-white/30 hover:text-primary hover:bg-white/5"
                                                onClick={() => fileInputRef.current?.click()}
                                            >
                                                <Plus className="h-5 w-5" />
                                            </Button>

                                            <div className="flex-1 relative">
                                                <Input
                                                    value={input}
                                                    onChange={(e) => setInput(e.target.value)}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                                                    placeholder="Sync command..."
                                                    className={`h-10 pr-10 text-xs ${GLASS_INPUT} border-white/10 focus:border-primary/50 focus:ring-0 rounded-xl`}
                                                />
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="absolute right-1 top-0.5 h-9 w-9 rounded-full text-white/30 hover:text-primary transition-colors"
                                                    onClick={toggleMic}
                                                >
                                                    <Mic className={cn("h-4 w-4", isListening && "text-red-500 animate-pulse")} />
                                                </Button>
                                            </div>
                                            <Button
                                                size="icon"
                                                className="h-10 w-10 shrink-0 rounded-xl shadow-lg shadow-primary/20 bg-primary hover:bg-primary/90 text-black border-0"
                                                onClick={() => handleSend()}
                                                disabled={(!input.trim() && attachments.length === 0) || isThinking}
                                            >
                                                <Send className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </CardFooter>
                                </>
                            )}

                            {isMinimized && (
                                <div className="flex-1 flex items-center justify-between px-4 cursor-pointer group" onClick={() => setIsMinimized(false)}>
                                    <div className="flex items-center gap-3">
                                        <div className="relative">
                                            <div className="w-2.5 h-2.5 bg-[#c1ff00] rounded-full animate-pulse shadow-[0_0_8px_rgba(193,255,0,0.5)]" />
                                            {isThinking && <div className="absolute inset-0 rounded-full border-2 border-primary animate-ping" />}
                                        </div>
                                        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/70 group-hover:text-primary transition-colors">
                                            Active Monitoring...
                                        </span>
                                    </div>
                                    <Maximize2 className="h-3.5 w-3.5 text-white/30 group-hover:text-white transition-colors" />
                                </div>
                            )}
                        </Card>
                    </motion.div>
                )}
            </AnimatePresence>

            <FieldWorkOrderPad
                open={woSheetOpen}
                onOpenChange={setWoSheetOpen}
                activeWorkOrder={workOrders.find((wo) => wo.id === activeWoId) || null}
                workOrders={workOrders}
                onSelectWorkOrder={setActiveWoId}
                onSaveWorkOrder={handleUpdateWorkOrder}
                vehicleLabel=""
                currentTimeLabel={new Date().toLocaleTimeString()}
                elapsedLabel="00:00:00"
                activeMechanic={currentUser || ''}
            />
        </div>
    );
}
